/**
 * Multi-language color tokens → #RRGGBB.
 * Compound phrases are matched longest-first in normalizeColor.
 */

export function normalizeColorKey(raw: string): string {
  return raw
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Tokens that must not drive a swatch in “last color word” resolution
 * (e.g. “multi color” → avoid matching “blue” wrongly).
 */
export const TAIL_COLOR_TOKEN_STOPLIST = new Set([
  'color',
  'colour',
  'colors',
  'colours',
  'teinte',
  'teintes',
  'nuance',
  'nuances',
  'tone',
  'tones',
  'shade',
  'shades',
  'multicolor',
  'multicolour',
  'multi-color',
  'multi',
  'assorted',
  'various',
])

/**
 * Lemmas not present in SIMPLE_COLOR_HEX → canonical key in SIMPLE_COLOR_HEX.
 * Used only for multi-word tail resolution (and only after direct lookup fails).
 */
export const COLOR_TOKEN_ALIASES: Record<string, string> = {
  marine: 'navy',
  sapin: 'green',
  emeraude: 'emerald',
  rubis: 'ruby',
  ciel: 'sky',
  poussiere: 'gray',
}

/** Single-word or canonical token → hex (keys already lowercased ASCII or unicode). */
export const SIMPLE_COLOR_HEX: Record<string, string> = {
  // English
  red: '#FF0000',
  crimson: '#DC143C',
  scarlet: '#FF2400',
  cherry: '#DE3163',
  /** Fashion “rose” / pink (FR/EN apparel). */
  rose: '#FFC0CB',
  pink: '#FFC0CB',
  hotpink: '#FF69B4',
  magenta: '#FF00FF',
  fuchsia: '#FF00FF',
  purple: '#800080',
  violet: '#8F00FF',
  indigo: '#4B0082',
  blue: '#0000FF',
  navy: '#000080',
  azure: '#007FFF',
  cyan: '#00FFFF',
  teal: '#008080',
  turquoise: '#40E0D0',
  aqua: '#00FFFF',
  green: '#008000',
  lime: '#00FF00',
  olive: '#808000',
  yellow: '#FFFF00',
  gold: '#FFD700',
  amber: '#FFBF00',
  orange: '#FFA500',
  brown: '#964B00',
  maroon: '#800000',
  beige: '#F5F5DC',
  ivory: '#FFFFF0',
  cream: '#FFFDD0',
  white: '#FFFFFF',
  snow: '#FFFAFA',
  black: '#000000',
  gray: '#808080',
  grey: '#808080',
  silver: '#C0C0C0',
  charcoal: '#36454F',
  tan: '#D2B48C',
  khaki: '#C3B091',
  coral: '#FF7F50',
  salmon: '#FA8072',
  peach: '#FFE5B4',
  mint: '#98FF98',
  lavender: '#E6E6FA',
  lilac: '#C8A2C8',
  burgundy: '#800020',
  wine: '#722F37',
  copper: '#B87333',
  bronze: '#CD7F32',
  rust: '#B7410E',
  mustard: '#FFDB58',
  mintgreen: '#98FB98',
  forest: '#228B22',
  sky: '#87CEEB',
  royalblue: '#4169E1',
  midnightblue: '#191970',
  darkblue: '#00008B',
  lightblue: '#ADD8E6',
  lightgreen: '#90EE90',
  darkgreen: '#006400',
  darkred: '#8B0000',
  lightgray: '#D3D3D3',
  lightgrey: '#D3D3D3',
  darkgray: '#A9A9A9',
  darkgrey: '#A9A9A9',
  offwhite: '#FAF9F6',
  nude: '#E3BC9E',
  camel: '#C19A6B',
  chocolate: '#7B3F00',
  coffee: '#6F4E37',
  denim: '#1560BD',
  denimblue: '#2243B6',
  multicolor: '#888888',
  'multi-color': '#888888',
  multicolour: '#888888',
  assorted: '#888888',
  clear: '#E8E8E8',
  transparent: '#E8E8E8',
  natural: '#D4C4A8',
  nature: '#D4C4A8',
  plum: '#8E4585',
  sage: '#87AE73',
  emerald: '#50C878',
  ruby: '#E0115F',
  slate: '#708090',
  pearl: '#F0EAD6',
  sapphire: '#0F52BA',
  sand: '#C2B280',
  cerise: '#DE3163',
  aubergine: '#614051',
  eggplant: '#614051',
  mahogany: '#C04000',
  oxblood: '#4A0000',
  brick: '#CB4154',
  steel: '#71797E',
  sandstone: '#786D5F',
  stone: '#928E85',
  fog: '#DCDCDC',
  mist: '#E4E4E4',
  earth: '#8B7355',

  // French
  rouge: '#FF0000',
  bordeaux: '#6D071A',
  rosepale: '#FFB6C1',
  mauve: '#E0B0FF',
  pourpre: '#A020F0',
  bleu: '#0000FF',
  vert: '#008000',
  vertclair: '#90EE90',
  vertolive: '#808000',
  jaune: '#FFFF00',
  jauneclair: '#FFFFE0',
  or: '#FFD700',
  argent: '#C0C0C0',
  blanc: '#FFFFFF',
  blanccass: '#FAF9F6',
  noir: '#000000',
  gris: '#808080',
  grisclair: '#D3D3D3',
  grisfonce: '#505050',
  marron: '#964B00',
  ivoire: '#FFFFF0',
  peche: '#FFE5B4',
  corail: '#FF7F50',
  saumon: '#FA8072',
  dore: '#FFD700',
  cuivre: '#B87333',
  anthracite: '#2E2E2E',
  ecru: '#C2B280',
  taupe: '#483C32',
  parme: '#CFA0E9',
  menthe: '#98FF98',
  citron: '#FFF700',
  framboise: '#E30B5C',
  lavande: '#E6E6FA',

  // Spanish
  rojo: '#FF0000',
  azul: '#0000FF',
  azulmarino: '#000080',
  azuloscuro: '#00008B',
  verde: '#008000',
  amarillo: '#FFFF00',
  naranja: '#FFA500',
  negro: '#000000',
  blanco: '#FFFFFF',
  morado: '#800080',
  rosa: '#FFC0CB',
  dorado: '#FFD700',
  plateado: '#C0C0C0',
  cafe: '#6F4E37',
  caoba: '#C04000',
  celeste: '#87CEEB',
  violeta: '#8F00FF',
  lima: '#00FF00',
  limon: '#FFF700',
  turquesa: '#40E0D0',

  // German
  rot: '#FF0000',
  blau: '#0000FF',
  grun: '#008000',
  gruen: '#008000',
  gelb: '#FFFF00',
  schwarz: '#000000',
  weiss: '#FFFFFF',
  weiß: '#FFFFFF',
  grau: '#808080',
  braun: '#964B00',
  lila: '#800080',
  silber: '#C0C0C0',

  // Arabic (script keys after normalize may stay — NFKD keeps Arabic letters)
  احمر: '#FF0000',
  أحمر: '#FF0000',
  ازرق: '#0000FF',
  أزرق: '#0000FF',
  اخضر: '#008000',
  أخضر: '#008000',
  اصفر: '#FFFF00',
  أصفر: '#FFFF00',
  اسود: '#000000',
  أسود: '#000000',
  ابيض: '#FFFFFF',
  أبيض: '#FFFFFF',
  رمادي: '#808080',
  بني: '#964B00',
  برتقالي: '#FFA500',
  وردي: '#FFC0CB',
  ذهبي: '#FFD700',
  فضي: '#C0C0C0',
  بنفسجي: '#800080',

  // Chinese (common)
  红: '#FF0000',
  蓝: '#0000FF',
  绿: '#008000',
  黄: '#FFFF00',
  黑: '#000000',
  白: '#FFFFFF',
  灰: '#808080',
  橙: '#FFA500',
  粉: '#FFC0CB',
  紫: '#800080',
  棕: '#964B00',
  金: '#FFD700',
  银: '#C0C0C0',
}

/** Multi-word phrases → hex, matched longest-first. */
export const COMPOUND_COLOR_HEX_ENTRIES: [string, string][] = [
  ['bleu nuit', '#191970'],
  ['bleu marine', '#000C66'],
  ['bleu roi', '#4169E1'],
  ['bleu ciel', '#87CEEB'],
  ['bleu clair', '#ADD8E6'],
  ['bleu canard', '#0F7173'],
  ['bleu petrole', '#1B4B5A'],
  ['bleu turquoise', '#00CED1'],
  ['bleu electrique', '#7DF9FF'],
  ['dark blue', '#00008B'],
  ['light blue', '#ADD8E6'],
  ['navy blue', '#000080'],
  ['royal blue', '#4169E1'],
  ['sky blue', '#87CEEB'],
  ['midnight blue', '#191970'],
  ['baby blue', '#89CFF0'],
  ['powder blue', '#B0E0E6'],
  ['teal blue', '#367588'],
  ['steel blue', '#4682B4'],
  ['cobalt blue', '#0047AB'],
  ['ice blue', '#99FFFF'],
  ['ocean blue', '#4F97A3'],
  ['azul oscuro', '#00008B'],
  ['azul marino', '#000080'],
  ['azul claro', '#ADD8E6'],
  ['azul celeste', '#87CEEB'],
  ['verde oscuro', '#006400'],
  ['verde claro', '#90EE90'],
  ['verde menta', '#98FF98'],
  ['verde oliva', '#808000'],
  ['verde esmeralda', '#50C878'],
  ['dark green', '#006400'],
  ['light green', '#90EE90'],
  ['forest green', '#228B22'],
  ['olive green', '#808000'],
  ['lime green', '#32CD32'],
  ['neon green', '#39FF14'],
  ['pastel green', '#77DD77'],
  ['dark red', '#8B0000'],
  ['light red', '#FF6868'],
  ['blood red', '#660000'],
  ['ruby red', '#E0115F'],
  ['fire red', '#FF4500'],
  ['cherry red', '#990012'],
  ['wine red', '#722F37'],
  ['dark gray', '#A9A9A9'],
  ['dark grey', '#A9A9A9'],
  ['light gray', '#D3D3D3'],
  ['light grey', '#D3D3D3'],
  ['charcoal gray', '#36454F'],
  ['charcoal grey', '#36454F'],
  ['rose pale', '#FADADD'],
  ['rose gold', '#B76E79'],
  ['rose poudre', '#EDC0C0'],
  ['rose poudré', '#EDC0C0'],
  ['vieux rose', '#C08081'],
  ['terracotta', '#E2725B'],
  ['terre cuite', '#E2725B'],
  ['sand beige', '#C2B280'],
  ['sand', '#C2B280'],
  ['off white', '#FAF9F6'],
  ['off-white', '#FAF9F6'],
  ['pearl white', '#F8F6F0'],
  ['snow white', '#FFFAFA'],
  ['matte black', '#1C1C1C'],
  ['jet black', '#0A0A0A'],
  ['cream white', '#FFFDD0'],
  ['ivory white', '#FFFFF0'],
  ['camel beige', '#C19A6B'],
  ['champagne', '#F7E7CE'],
  ['mocha', '#967969'],
  ['espresso', '#3C2415'],
  ['coral pink', '#FF7F7F'],
  ['hot pink', '#FF69B4'],
  ['dusty pink', '#DCAE96'],
  ['blush pink', '#DE5D83'],
  ['millennial pink', '#F3CFC6'],
  ['burnt orange', '#CC5500'],
  ['deep orange', '#FF8C00'],
  ['golden yellow', '#FFDF00'],
  ['lemon yellow', '#FFF44F'],
  ['pastel yellow', '#FFF9C4'],
  ['pastel pink', '#FFD1DC'],
  ['pastel blue', '#AEC6CF'],
  ['pastel purple', '#B39EB5'],
  ['electric blue', '#7DF9FF'],
  ['fluo yellow', '#CCFF00'],
  ['fluo green', '#39FF14'],
  ['fluo pink', '#FF1493'],
  ['anthracite gray', '#2E2E2E'],
  ['anthracite grey', '#2E2E2E'],
  ['taupe gray', '#8B8589'],
  ['stone gray', '#928E85'],
  ['wolf gray', '#504A4B'],
  ['khaki green', '#6B8E23'],
  ['military green', '#4B5320'],
  ['camouflage', '#78866B'],
  ['denim blue', '#1560BD'],
  ['stone washed', '#7A89B8'],
  ['washed blue', '#A7C6ED'],
  ['washed black', '#3B3B3B'],
  ['antique white', '#FAEBD7'],
  ['ghost white', '#F8F8FF'],
  ['smoke gray', '#738276'],
  ['smoke grey', '#738276'],
]

/** Longest phrase first for greedy matching. */
export const COMPOUND_SORTED: [string, string][] = [...COMPOUND_COLOR_HEX_ENTRIES].sort(
  (a, b) => b[0].length - a[0].length,
)

/** One token → hex for tail resolution (stoplist + SIMPLE + aliases). */
export function hexForColorToken(token: string): string | null {
  if (!token || TAIL_COLOR_TOKEN_STOPLIST.has(token)) return null
  const direct = SIMPLE_COLOR_HEX[token]
  if (direct) return direct
  const mapped = COLOR_TOKEN_ALIASES[token]
  if (mapped) return SIMPLE_COLOR_HEX[mapped] ?? null
  return null
}

/** Right-to-left: first paintable hue (last reliable “color word” in the label). */
export function hexFromLastNamedToken(tokens: readonly string[]): string | null {
  for (let i = tokens.length - 1; i >= 0; i--) {
    const h = hexForColorToken(tokens[i])
    if (h) return h
  }
  return null
}
